import { useLazyQuery, useQuery } from "@apollo/client"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import PersonIcon from "@mui/icons-material/Person"
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
} from "@mui/material"
import { GET_CURRENT_WITNESSES, SEARCH_WITNESSES } from "graphql/queries/witnesses"
import { Contact, TestData } from "graphql/types"
import { debounce } from "lodash"
import { useCallback, useEffect, useMemo, useState } from "react"

type WitnessSelectionProps = {
  data: TestData
  // eslint-disable-next-line no-unused-vars
  setData: (arg: TestData) => void
}

export const WitnessSelection = (props: WitnessSelectionProps) => {
  const { data, setData } = props
  const [selectedWitnesses, setSelectedWitnesses] = useState<Contact[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [searchOptions, setSearchOptions] = useState<Contact[]>([])
  const [isSearching, setIsSearching] = useState(false)
  // Local cache for witness data to ensure persistence
  const [witnessCache] = useState<Map<string, Contact>>(new Map())

  // Fetch current witnesses (visitors on-site)
  const { data: currentWitnessesData, loading: loadingCurrent } = useQuery(GET_CURRENT_WITNESSES, {
    fetchPolicy: "cache-and-network",
  })

  // Don't fetch all witnesses initially - only via search
  // This prevents loading thousands of contacts on component mount

  // Search witnesses query (lazy)
  const [searchWitnesses, { loading: loadingSearch }] = useLazyQuery(SEARCH_WITNESSES, {
    fetchPolicy: "network-only",
  })

  // Current visitors on-site (only these show in initial dropdown)
  const currentWitnesses = useMemo(() => {
    const witnesses = currentWitnessesData?.getCurrentWitnesses || []
    // Cache current witnesses
    witnesses.forEach((w: Contact) => {
      if (w.id) witnessCache.set(w.id, w)
    })
    return witnesses
  }, [currentWitnessesData, witnessCache])

  // Helper: check if contact matches all tokens (case-insensitive)
  const matchesAllTokens = (contact: Contact, tokens: string[]) => {
    const haystack = [
      contact.firstName || "",
      contact.lastName || "",
      contact.displayName || "",
      contact.email || "",
      contact.companyName || "",
    ]
      .join(" ")
      .toLowerCase()

    return tokens.every((t) => haystack.includes(t.toLowerCase()))
  }

  // Debounced search function with multi-token support (e.g., "Ryan Coulson")
  const debouncedSearch = useMemo(
    () =>
      debounce(async (term: string) => {
        const trimmed = term.trim()
        if (trimmed.length >= 2) {
          setIsSearching(true)
          try {
            const tokens = trimmed.split(/\s+/).filter((t) => t.length > 0)
            
            // First, always search with the full term as-is
            const fullTermResult = await searchWitnesses({
              variables: { searchTerm: trimmed, limit: 40 },
            })
            const fullTermItems: Contact[] = fullTermResult.data?.searchWitnesses || []
            
            // Also check current witnesses that match
            const matchingCurrentWitnesses = currentWitnesses.filter((w: Contact) => 
              matchesAllTokens(w, tokens)
            )
            
            // Combine results by ID to avoid duplicates
            const byId = new Map<string, Contact>()
            
            // Add full term search results
            fullTermItems.forEach((w) => {
              if (w?.id) byId.set(w.id, w)
            })
            
            // Add matching current witnesses
            matchingCurrentWitnesses.forEach((w: Contact) => {
              if (w?.id) byId.set(w.id, w)
            })

            // If we have multiple tokens and not enough results, also search individual tokens
            if (tokens.length > 1 && byId.size < 10) {
              const tokenResults = await Promise.all(
                tokens.map((tk) => searchWitnesses({ variables: { searchTerm: tk, limit: 30 } }))
              )
              
              // Add token search results that match all tokens
              tokenResults.forEach((res) => {
                const arr: Contact[] = res.data?.searchWitnesses || []
                arr.forEach((w) => {
                  if (w?.id && matchesAllTokens(w, tokens)) {
                    byId.set(w.id, w)
                  }
                })
              })
            }

            // Persist to cache and set options
            const unified = Array.from(byId.values())
            unified.forEach((w) => w?.id && witnessCache.set(w.id, w))
            setSearchOptions(unified)
          } catch (error) {
            console.error("Search error:", error)
          } finally {
            setIsSearching(false)
          }
        } else {
          setSearchOptions([])
        }
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchWitnesses, currentWitnesses]
  )

  // Handle search input change
  const handleSearchChange = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      setSearchTerm(value)
      debouncedSearch(value)
    },
    [debouncedSearch]
  )

  // Initialize selected witnesses from data
  useEffect(() => {
    if (data.witnesses?.length > 0) {
      // Convert witnesses to Contact type if needed
      const witnesses = data.witnesses
        .map((witness) => {
          // If witness is already a Contact with all needed fields, use it directly
          if (witness && typeof witness === "object" && "id" in witness) {
            const contactWitness = witness as Contact

            // Try to get complete data from cache if available
            const cachedWitness = witnessCache.get(contactWitness.id)
            if (cachedWitness) {
              // Use cached version which should have all fields
              return cachedWitness
            }

            // Cache this witness for future use
            witnessCache.set(contactWitness.id, contactWitness)

            // If firstName/lastName are missing but we have displayName, try to parse it
            if (
              !contactWitness.firstName &&
              !contactWitness.lastName &&
              contactWitness.displayName
            ) {
              const parts = contactWitness.displayName.split(" ")
              if (parts.length >= 2) {
                contactWitness.firstName = parts[0]
                contactWitness.lastName = parts.slice(1).join(" ")
              }
            }
            return contactWitness
          }
          return null
        })
        .filter((w): w is Contact => w !== null)

      setSelectedWitnesses(witnesses)
    }
  }, [data.witnesses, witnessCache])

  // Options for autocomplete
  // Only show current witnesses initially, use search for all contacts
  const options = searchTerm.length >= 2 ? searchOptions : currentWitnesses

  // Format witness display name
  const getWitnessLabel = (witness: Contact | null) => {
    if (!witness) return ""
    const name =
      witness.displayName || `${witness.firstName || ""} ${witness.lastName || ""}`.trim()
    const company = witness.companyName ? ` (${witness.companyName})` : ""
    return name + company
  }

  // Check if witness is currently on-site
  const isCurrentVisitor = (witness: Contact | null) => {
    if (!witness) return false
    return currentWitnesses.some((current: Contact) => current.id === witness.id)
  }

  const loading = loadingCurrent

  return (
    <Autocomplete
      sx={{ mt: 1 }}
      size="small"
      multiple
      id="witness-selection"
      options={options}
      getOptionLabel={getWitnessLabel}
      filterSelectedOptions
      filterOptions={(x) => x} // Disable MUI's built-in filtering since we handle it server-side
      value={selectedWitnesses}
      loading={loading || isSearching || loadingSearch}
      onInputChange={handleSearchChange}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Zeugen / Witnesses"
          placeholder="Tippen für Suche / Type to search..."
          helperText={
            searchTerm.length === 0
              ? "Zeigt aktuelle Besucher. Tippen Sie um alle Kontakte zu durchsuchen."
              : null
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(loading || isSearching || loadingSearch) && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        if (!option) return null
        return (
          <li {...props} key={option.id}>
            <Box display="flex" alignItems="center" width="100%">
              <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: "primary.light" }}>
                <PersonIcon fontSize="small" />
              </Avatar>
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <span>
                    <strong>
                      {option.firstName} {option.lastName}
                    </strong>
                  </span>
                  {isCurrentVisitor(option) && (
                    <Tooltip title="Aktuell vor Ort / Currently on-site">
                      <Chip
                        size="small"
                        icon={<LocationOnIcon />}
                        label="Vor Ort"
                        color="success"
                        sx={{ height: 20 }}
                      />
                    </Tooltip>
                  )}
                </Box>
                {option.companyName && (
                  <Box fontSize="0.875rem" color="text.secondary">
                    {option.companyName}
                  </Box>
                )}
                {option.email && (
                  <Box fontSize="0.75rem" color="text.secondary">
                    {option.email}
                  </Box>
                )}
              </Box>
            </Box>
          </li>
        )
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          if (!option) return null
          return (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              avatar={
                <Avatar sx={{ bgcolor: "primary.light" }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
              }
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  <span>{`${option.firstName || ""} ${option.lastName || ""}`}</span>
                  {isCurrentVisitor(option) && <LocationOnIcon fontSize="small" color="success" />}
                </Box>
              }
              size="small"
            />
          )
        })
      }
      onChange={(_, value) => {
        setSelectedWitnesses(value as Contact[])
        // Cache selected witnesses and ensure we have complete data
        value.forEach((w) => {
          if (w?.id) witnessCache.set(w.id, w)
        })
        // Convert Contact objects to the format needed by TestData
        setData({ ...data, witnesses: value })
      }}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      noOptionsText={
        searchTerm.length >= 2
          ? "Keine Zeugen gefunden"
          : "Geben Sie mindestens 2 Zeichen ein, um zu suchen"
      }
    />
  )
}
